import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { AssetDto } from '../models/asset.models';
import { CustomFieldDto } from '../models/project-custom-fields.models';
import { ProjectRulesDto } from '../models/project-rules.models';
import { ZoneDto } from '../models/project-zones.models';

export type ProjectTagDto = {
  serial: string;
  model: string;
  notification_enabled: boolean;
};

export type ProjectDto = {
  _id: string;
  name: string;
  description?: string;
  client_id?: string;
  tags?: ProjectTagDto[];
  gateways?: {
    serial: string;
    model: string;
    send_info_to_tag_enabled: boolean;
    zone_id?: string | null;
    range?: number;
  }[];
  custom_fields?: Array<Omit<CustomFieldDto, 'project_id'> & { _id?: string; project_id?: string }>;
  rules?: ProjectRulesDto;
  assets?: AssetDto[];
  zones?: ZoneDto[];
};

export type ClientDto = {
  _id: string;
  name: string;
  cnpj?: string;
  logo?: string;
  email?: string;
  address?: string;
  projects?: ProjectDto[];
};

export type CreateClientInputDto = {
  name: string;
  cnpj: string;
  email: string;
  address: string;
};

export type UpdateClientInputDto = Partial<CreateClientInputDto>;

export type CreateProjectInputDto = {
  name: string;
  client_id: string;
  tags: ProjectTagDto[];
  gateways: {
    serial: string;
    model?: string;
    send_info_to_tag_enabled?: boolean;
    zone_id: string | null;
    range?: number;
  }[];
  custom_fields: {
    name: string;
    label: string;
    options: string[];
  }[];
  rules?: ProjectRulesDto;
};

export type UpdateProjectInputDto = Omit<Partial<CreateProjectInputDto>, 'rules'> & {
  rules?: Partial<ProjectRulesDto>;
};

export type GatewayStateDto = {
  gateway_id: string;
  online: boolean | null;
  date_change: number | null;
  last_seen: number | null;
};

export type GatewayConnectivityOutageDto = {
  disconnected_at: number | null;
  reconnected_at: number | null;
  offline_ms: number;
  starts_before_period: boolean;
};

export type GatewayConnectivityDto = {
  gateway_id: string;
  start: number;
  end: number;
  uptime_percent: number | null;
  offline_ms: number;
  total_ms: number;
  outages: GatewayConnectivityOutageDto[];
};

@Injectable({ providedIn: 'root' })
export class ProjectsApiService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getById(id: string) {
    return this.http.get<ProjectDto>(`${this.baseUrl}/projects/${encodeURIComponent(id)}`);
  }

  listClients(populated: boolean = false) {
    return this.http.get<ClientDto[]>(`${this.baseUrl}/clients?pop=${populated}`);
  }

  listProjects(params?: { client_id?: string; ids?: string[] }) {
    const search = new URLSearchParams();
    if (params?.client_id) search.set('client_id', params.client_id);
    if (params?.ids?.length) search.set('ids', params.ids.join(','));
    const query = search.toString();
    return this.http.get<ProjectDto[]>(`${this.baseUrl}/projects${query ? `?${query}` : ''}`);
  }

  createClient(input: CreateClientInputDto) {
    return this.http.post<ClientDto>(`${this.baseUrl}/clients`, input);
  }

  updateClient(id: string, input: UpdateClientInputDto) {
    return this.http.put<ClientDto>(`${this.baseUrl}/clients/${encodeURIComponent(id)}`, input);
  }

  deleteClient(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/clients/${encodeURIComponent(id)}`, {
      observe: 'response',
    });
  }

  createProject(input: CreateProjectInputDto) {
    return this.http.post<ProjectDto>(`${this.baseUrl}/projects`, input);
  }

  updateProject(id: string, input: UpdateProjectInputDto) {
    return this.http.put<ProjectDto>(`${this.baseUrl}/projects/${encodeURIComponent(id)}`, input);
  }

  deleteProject(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/projects/${encodeURIComponent(id)}`, {
      observe: 'response',
    });
  }

  getGatewayConnectivity(
    projectId: string,
    gatewayId: string,
    range: { start: number; end: number },
  ) {
    const query = new URLSearchParams({
      gateway_id: gatewayId,
      start: String(range.start),
      end: String(range.end),
    });
    return this.http.get<GatewayConnectivityDto>(
      `${this.baseUrl}/projects/${encodeURIComponent(projectId)}/gateway-connectivity?${query.toString()}`,
    );
  }

  getGatewayStates(projectId: string) {
    return this.http.get<GatewayStateDto[]>(
      `${this.baseUrl}/projects/${encodeURIComponent(projectId)}/gateway-states`,
    );
  }
}
